/**
 * AZK binding types module
 * Types for managing station bindings between OKKO and competitor stations
 */

/**
 * Station information for binding
 * @property {number} id - Station ID
 * @property {string} station_number - Station number
 * @property {number} unicode - Station unicode identifier
 * @property {string} name - Station name
 * @property {string} address - Station address
 * @property {string} [settlement_name] - City/settlement name for sorting
 * @property {string} region - Region name
 * @property {string} province - Province name
 * @property {number} latitude - Latitude coordinate
 * @property {number} longitude - Longitude coordinate
 * @property {string} status - Station status
 * @property {string} brand - Brand name
 */
export interface Station {
  id: number;
  station_number: string;
  unicode?: number;
  name?: string;
  address?: string;
  settlement_name?: string;
  region?: string;
  province?: string;
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
  status?: string;
  brand?: string;
  [key: string]: any;
}

export function isStationWithUnicode(station: Station): station is Station & { unicode: number } {
  return station.unicode !== undefined && station.unicode !== null;
}

/**
 * Station binding information
 * @property {number} id - Binding ID
 * @property {number} okko_station_unicode - OKKO station unicode
 * @property {number} competitor_station_unicode - Competitor station unicode
 * @property {string} influence_type - Type of influence
 * @property {string} location_type - Type of location
 * @property {number} distance_to_azk - Distance to AZK
 * @property {number} straight_distance - Straight line distance
 * @property {string} okko_station_name - OKKO station name
 * @property {string} okko_station_address - OKKO station address
 * @property {string} competitor_station_name - Competitor station name
 * @property {string} competitor_station_address - Competitor station address
 * @property {string} okko_brand_name - OKKO brand name
 * @property {string} competitor_brand_name - Competitor brand name
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 */
export interface Binding {
  id: number;
  okko_station_unicode: number;
  competitor_station_unicode: number;
  influence_type: string;
  location_type: string;
  distance_to_azk: number;
  straight_distance: number;
  okko_station_name: string;
  okko_station_address: string;
  competitor_station_name: string;
  competitor_station_address: string;
  okko_brand_name: string;
  competitor_brand_name: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export type { ApiResponse } from './common.types';

/**
 * Filter options for competitor stations
 * @property {string[]} regions - Available regions
 * @property {string[]} province - Available provinces
 * @property {string[]} brands - Available brands
 * @property {Object} mappings - Filter mappings
 * @property {Object} mappings.region_province - Region to provinces mapping
 * @property {Object} mappings.region_brands - Region to brands mapping
 */
export interface Filters {
  regions: string[];
  province: string[];
  brands: string[];
  mappings?: {
    region_province: { [key: string]: string[] };
    region_brands: { [key: string]: string[] };
  };
}

/**
 * Form options for binding creation
 * @property {Array<{value: string, label: string}>} influence_types - Influence type options
 * @property {Array<{value: string, label: string}>} location_types - Location type options
 */
export interface FormOptions {
  influence_types: { value: string; label: string }[];
  location_types: { value: string; label: string }[];
}

/**
 * Bulk binding update request
 * @property {number} okkoStationUnicode - OKKO station unicode
 * @property {Array} bindings - Bindings to create/update
 * @property {number} [bindings[].id] - Binding ID (for UPDATE)
 * @property {number} bindings[].competitorStationUnicode - Competitor station unicode
 * @property {string} bindings[].influence_type - Influence type
 * @property {string} bindings[].location_type - Locaation type
 * @property {number} bindings[].distance_to_azk - Distance to AZK
 */
export interface BulkBindingRequest {
  okkoStationUnicode: number;
  bindings: {
    id?: number;
    competitorStationUnicode: number;
    influence_type_id?: number;
    location_type_id?: number;
    distance_to_azk?: number;
  }[];
}

/**
 * Bulk binding update response
 * @property {string} message - Response message
 * @property {Binding[]} data - Updated bindings
 * @property {any[]} errors - Any errors that occurred
 * @property {string} cid - Correlation ID
 */
export interface BulkBindingResponse {
  message: string;
  data: Binding[];
  errors: any[];
  cid: string;
}

export type SelectedFilters = {
  region: string;
  province: string;
  brand: string;
};

/**
 * Detailed binding information for display in the table
 */
export interface BindingDetail {
  /** Unique identifier for the binding */
  id: number;
  /** Station unicode identifier */
  unicode?: number;
  /** Station address */
  address?: string;
  /** Station brand name */
  brand?: string;
  /** Type of influence this station has */
  influence_type: string;
  /** Physical location type relative to reference station */
  location_type: string;
  /** Distance to the AZK station */
  distance_to_azk: string | number;
  /** Straight-line distance between stations */
  straight_distance: string | number;
  /** Whether this is an OKKO station */
  isOkko?: boolean;
}

/**
 * Detailed binding information for competitor view
 */
export interface CompetitorBindingDetail {
  okkoStationUnicode: number;
  okkoStation: Station | undefined;
  binding: {
    influence_type: string;
    location_type: string;
    distance_to_azk: string | number;
    straight_distance: string | number;
  };
}

/**
 * Data structure for binding updates sent to the API
 */
export interface BindingUpdateData {
  id?: number;
  competitorStationUnicode: number;
  influence_type_id?: number;
  location_type_id?: number;
  distance_to_azk?: number;
}

/**
 * Error structure returned from bulk update operations
 */
export interface BulkUpdateError {
  /** Error message describing what went wrong */
  error: string;
}
